import Chart, { useChart } from 'src/components/chart';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import Iconify from '../../iconify';
import CustomTooltip from './CustomTooltip';
import { PresenceSegments } from '../types';

/**
 * Compute the percentage of results where only competitors are present.
 * This is the residual after subtracting Organic only, Solo bidder,
 * and Top ad position segments from 100.
 *
 * @param data Presence distribution segments expressed as percentages (0–100).
 * @param data.organicOnly Percentage of results with organic presence only.
 * @param data.soloBidder Percentage of results where the brand is the only bidder.
 * @param data.topAdPosition Percentage of results where the brand holds a top ad position.
 * @returns Percentage (0–100) for the "Competitors only" segment.
 */
const calculateCompetitorsOnly = (data: PresenceSegments) => {
    return 100 - data.organicOnly - data.soloBidder - data.topAdPosition;
};

/**
 * PresenceRateChart renders a compact horizontal stacked bar to visualize a presence breakdown
 * across four segments: Organic only, Solo bidder, Top ad position, and Competitors only.
 * The chart is styled as a sparkline (no axes/legend) and reveals a detailed tooltip on hover.
 *
 * Notes:
 * - Expects raw percentage values (0–100) in the input segments.
 * - Values displayed in the tooltip are rounded and clamped for readability.
 * - Colors adapt to the current MUI theme mode.
 *
 * @param data Presence distribution segments used to build the stacked bar.
 * @param data.organicOnly Percentage of results with organic presence only.
 * @param data.soloBidder Percentage of results where the brand is the only bidder.
 * @param data.topAdPosition Percentage of results where the brand holds a top ad position.
 * @param [height=40] Optional chart height in pixels.
 * @returns A React element containing the stacked bar chart with tooltip.
 */
export default function PresenceRateChart({ data, height = 40 }: { data: PresenceSegments; height?: number }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const colors = isDark
        ? ['#18A155', '#C09910', '#F9DE81', '#545D68']
        : ['#18A155', '#C09910', '#F9DE81', '#999'];
    const chartOptions = useChart({
        chart: {
            type: 'bar',
            stacked: true,
            parentHeightOffset: 0,
            animations: { enabled: false },
            sparkline: {
                enabled: true,
            },
            toolbar: {
                show: false,
            },
            stackType: 'normal',
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '30%',
                borderRadius: 0,
            },
        },
        stroke: {
            width: 0,
            show: false,
        },
        series: [
            { name: 'Organic only', data: [data.organicOnly] },
            { name: 'Solo bidder', data: [data.soloBidder] },
            { name: 'Top ad position', data: [data.topAdPosition] },
            { name: 'Competitors only', data: [calculateCompetitorsOnly(data)] },
        ],
        colors,
        tooltip: {
            enabled: false,
            x: { show: false },
            marker: { show: false },
        },
        legend: {
            show: false,
        },
        grid: {
            show: false,
        },
        xaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            max: 100,
            min: 0,
        },
        yaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
    });

    const items = [
        { label: 'Organic only', value: Math.max(0, Math.round(data.organicOnly)), color: colors[0], icon: 'solar:global-linear' },
        { label: 'Solo bidder', value: Math.max(0, Math.round(data.soloBidder)), color: colors[1], icon: 'carbon:user' },
        { label: 'Top ad position', value: Math.max(0, Math.round(data.topAdPosition)), color: colors[2], icon: 'carbon:user-multiple' },
        { label: 'Competitors only', value: Math.max(0, Math.round(calculateCompetitorsOnly(data))), color: colors[3], icon: 'ri:sword-line' },
    ];

    return (
        <CustomTooltip
            placement="top"
            title={
                <Stack direction="column" spacing={0.75}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Presence breakdown
                    </Typography>
                    <Stack direction="column" spacing={0.5}>
                        {items.map((it) => (
                            <Stack key={it.label} direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Iconify icon={it.icon} sx={{ width: 16, height: 16, color: it.color }} />
                                    <Typography variant="caption">{it.label}</Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">{it.value}%</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            }
        >
            <Box sx={{ height, width: '100%', minWidth: 0 }}>
                <Chart
                    type="bar"
                    series={chartOptions.series}
                    options={chartOptions}
                    height={height}
                    width="100%"
                />
            </Box>
        </CustomTooltip>
    );
}


